import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../lib/store.ts';
import { UploadRidesDialog, type UploadSection } from './UploadRidesDialog.tsx';
import { LucideIcon, type LucideName } from './icons/LucideIcon.tsx';

const ROWS: readonly { section: UploadSection; icon: LucideName; pro: boolean }[] = [
  { section: 'files', icon: 'file-up', pro: false },
  { section: 'history', icon: 'activity', pro: true },
];

/**
 * "Import rides" at the bottom of Profile → Integrations
 * (docs/RIDE_SOURCES_UI_PLAN.md §4.2): ride files, and the history import
 * (a ZIP or a data-export link, Pro), each opening the upload dialog there.
 */
export function ImportRidesCard() {
  const { t } = useTranslation();
  const isPro = useStore((s) => s.user?.plan === 'pro');
  const [open, setOpen] = useState<UploadSection | null>(null);
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
        {t('sources.import.title')}
      </h3>
      {ROWS.map((row) => (
        <button
          key={row.section}
          type="button"
          onClick={() => setOpen(row.section)}
          className="w-full text-left rounded-xl bg-(--color-bg-secondary) border border-(--color-border) hover:border-(--color-accent)/50 p-4 flex items-start gap-3"
        >
          <LucideIcon name={row.icon} size={20} className="mt-0.5 text-(--color-text-secondary)" />
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold text-(--color-text-primary)">
              {t(`sources.import.${row.section}`)}
            </span>
            <span className="block text-xs text-(--color-text-secondary) mt-0.5">
              {t(`sources.import.${row.section}Body`)}
            </span>
          </span>
          {row.pro && !isPro && (
            <span className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium border border-(--color-accent)/40 text-(--color-accent)">
              {t('sources.import.pro')}
            </span>
          )}
        </button>
      ))}
      {open && <UploadRidesDialog section={open} onClose={() => setOpen(null)} />}
    </section>
  );
}
